import { Copy, Pencil, Ticket, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { useBusiness } from '@/contexts/BusinessContext'

import { useCouponsTable } from '@/hooks/features/coupons'
import { useDataTable } from '@/hooks/ui'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'

import {
  ActionDropdown,
  ConfirmDialog,
  PageHeader,
  StatsCards,
  StatusBadge,
} from '@/components/atoms'

import { CouponModel } from '@/types'

import { CouponDialog } from './CouponDialog'
import { ReviewBanner } from '@/components/assistant/ReviewBanner'
import { useCouponProposal } from '@/components/assistant/useCouponProposal'

type TableColumns =
  | 'coupon_code'
  | 'discount_type'
  | 'discount'
  | 'expiry_date'
  | 'min_order'
  | 'max_discount'
  | 'status'
  | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
  'coupon_code',
  'discount_type',
  'discount',
  'expiry_date',
  'min_order',
  'max_discount',
  'status',
  'actions',
]

export const CouponsTable = () => {
  const {
    loading,
    data,
    pagination,
    stats,
    mode,
    couponDialogOpen,
    editCoupon,
    deleteDialogOpen,
    couponToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateCoupon,
    handleUpdateCoupon,
    toggleStatus,
  } = useCouponsTable()
  const review = useCouponProposal(openCreateDialog)
  const { formatCurrency } = useBusiness()

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code)
    toast.success('Coupon code copied to clipboard')
  }

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    CouponModel.Coupon,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'coupon_code',
        columnLabel: () => 'Code',
        cell: (data) => (
          <div className="flex items-center gap-1">
            <span className="font-mono text-sm font-medium text-ink">{data.coupon_code}</span>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Copy code"
              title="Copy code"
              onClick={() => copyCode(data.coupon_code)}
            >
              <Copy className="text-muted-foreground" />
            </Button>
          </div>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'discount_type',
        columnLabel: () => 'Type',
        cell: (data) => (
          <Badge tone="soft">
            {data.discount_type === 'percentage'
              ? 'Percentage'
              : data.discount_type === 'fixed_amount'
                ? 'Fixed amount'
                : 'Cash'}
          </Badge>
        ),
      },
      {
        accessorKey: 'discount',
        columnLabel: () => 'Discount',
        cell: (data) => (
          <span className="font-medium tabular-nums text-ink">
            {data.discount_type === 'percentage' ? `${data.discount}%` : formatCurrency(data.discount)}
          </span>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'expiry_date',
        columnLabel: () => 'Expires',
        cell: (data) => (
          <span className="tabular-nums text-text-2">
            {new Date(data.expiry_date).toLocaleDateString()}
          </span>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'min_order',
        columnLabel: () => 'Minimum order',
        cell: (data) => (
          <span className="tabular-nums text-text-2">
            {data.min_order_amount ? formatCurrency(data.min_order_amount) : 'N/A'}
          </span>
        ),
      },
      {
        accessorKey: 'max_discount',
        columnLabel: () => 'Maximum discount',
        cell: (data) => (
          <span className="tabular-nums text-text-2">
            {data.max_discount_amount ? formatCurrency(data.max_discount_amount) : 'N/A'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        columnLabel: () => 'Status',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <StatusBadge type="common" status={data.status} />
            <Switch checked={data.status === 'active'} onCheckedChange={() => toggleStatus(data)} />
          </div>
        ),
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end">
            <ActionDropdown
              items={[
                {
                  label: 'Edit',
                  permission: 'coupons:update',
                  icon: Pencil,
                  onClick: () => openEditDialog(data),
                },
                {
                  label: 'Delete',
                  permission: 'coupons:delete',
                  icon: Trash2,
                  variant: 'destructive',
                  onClick: () => handleDeleteClick(data),
                },
              ]}
            />
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Coupons"
        description="Manage discounts and promotional codes."
        onAddClick={openCreateDialog}
        addPermission="coupons:create"
        addButtonText="Create coupon"
      />

      <StatsCards
        stats={[
          {
            title: 'Total coupons',
            value: stats.totalCoupons,
            icon: Ticket,
          },
          {
            title: 'Active coupons',
            value: stats.activeCoupons,
          },
          {
            title: 'Inactive coupons',
            value: stats.inactiveCoupons,
          },
        ]}
      />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <Pagination.PageSizeDropdown
              pageSize={pagination.pageSize}
              onPageSizeChange={setPageSize}
            />
            <ColumnVisibilityDropdown
              columns={columns}
              onColumnVisibilityChange={changeColumnVisibility}
              disabledColumns={disabledColumns}
            />
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              title="No coupons found"
              btnText="Create coupon"
              openCreateDialog={openCreateDialog}
              createPermission="coupons:create"
            />
          ) : (
            <>
              <DataTable columns={columns} data={data} loading={loading} />

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Coupon Dialog */}
      <CouponDialog
        open={couponDialogOpen}
        mode={mode}
        editCoupon={editCoupon}
        closeDialog={closeDialog}
        onCreate={async (data) => {
          if ((await handleCreateCoupon(data)) && mode === 'create') review.onSaved()
        }}
        onUpdate={handleUpdateCoupon}
        loading={createLoading || updateLoading}
        prefill={mode === 'create' ? review.prefill : undefined}
        notice={mode === 'create' && review.proposal ? <ReviewBanner mode="save" onDiscard={() => { review.discard(); closeDialog() }} /> : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete coupon?"
        description={`This will permanently delete the coupon "${couponToDelete?.coupon_code}". This action cannot be undone.`}
        confirmText={deleteLoading ? 'Deleting…' : 'Delete coupon'}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        variant="destructive"
      />
    </div>
  )
}
